import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
  getAllUsers,
  createUser,
  updateUser,
  deleteUser,
  getUserById
} from "../../../services/userManagementService";
import {
  User,
  UserCreatePayload,
  UserUpdatePayload,
  PaginatedUsers
} from "../../../types/userManagementTypes";

export const useUserManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<PaginatedUsers>({
    data: [],
    pagination: {
      total: 0,
      page: 1,
      limit: 10
    }
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const fetchUsers = useCallback(async (filters: { [key: string]: string | number | null } = {}) => {
    setLoading(true);
    try {
      const response = await getAllUsers(filters);
      setUsers(response.data);
      setError(null);
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_fetch_manager"));
      setError(t("failed_to_fetch_manager"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleCreate = async (payload: UserCreatePayload) => {
    try {
      await createUser(payload);
      message.success(t("user_manager_created_successfully"));
      await fetchUsers();
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_create_manager"));
      return false;
    }
  };

  const handleUpdate = async (userId: string, payload: UserUpdatePayload) => {
    try {
      await updateUser(userId, payload);
      message.success(t("user_manager_updated_successfully"));
      await fetchUsers();
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_update_manager"));
      return false;
    }
  };

  const handleDelete = async (userId: string) => {
    try {
      await deleteUser(userId);
      message.success(t("user_manager_deleted_successfully"));
      await fetchUsers();
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_delete_manager"));
      return false;
    }
  };

  const handleGetUserById = async (userId: string) => {
    try {
      const response = await getUserById(userId);
      return response?.data;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_delete_manager"));
      return false;
    }
  };


  return {
    users: users.data,
    infoPagination: {
      total: users.pagination.total,
      page: users.pagination.page,
      limit: users.pagination.limit,
    },
    loading,
    error,
    selectedUser,
    setSelectedUser,
    isModalOpen,
    setIsModalOpen,
    fetchUsers,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleGetUserById
  };
};

export default useUserManagement;
